import { App, Button, Flex } from "antd";
import { FC, useCallback, useRef, useState } from "react";

import { latencyIcons } from "@/constants/ui/latencyIcons";
import { useMultiTap } from "@/hooks";
import {
    useAppDispatch,
    useAppSelector,
    useOfflineModeSelector,
} from "@/hooks/redux";
import { BaseModal } from "@/shared/ui/BaseModal";
import { PrimaryText } from "@/shared/ui/Typography";
import { updateOfflineStatus } from "@/store/slices/offline";
import { isOnline } from "@/utils/network/isOnline";

import { StyledExclamationCircleOutlined } from "./Styled";
import { checkLatencyIcon } from "./utils/checkLatencyIcon";
import { formatLatency } from "./utils/formatLatency";

interface NetworkIndicatorProps {
    canChangeOfflineMode?: boolean;
}

export const NetworkIndicator: FC<NetworkIndicatorProps> = ({
    canChangeOfflineMode = false,
}) => {
    const { notification } = App.useApp();

    const dispatch = useAppDispatch();

    const offlineMode = useOfflineModeSelector();
    const latency = useAppSelector((state) => state.offline.latency);
    const type = useAppSelector((state) => state.theme.type);

    const [openModal, setOpenModal] = useState(false);
    const openedAtRef = useRef(0);

    const handleOpenModal = useCallback(() => {
        openedAtRef.current = Date.now();

        setTimeout(() => {
            setOpenModal(true);
        }, 0);
    }, []);

    const handleCloseModal = useCallback(() => {
        const justOpened = Date.now() - openedAtRef.current < 300;

        if (justOpened) {
            return;
        }

        setOpenModal(false);
    }, []);

    const handleDoubleTap = useMultiTap(handleOpenModal);

    const handleChangeMode = useCallback(async () => {
        if (offlineMode) {
            const online = await isOnline();

            if (!online) {
                dispatch(updateOfflineStatus(true));

                notification.error({
                    title: "Відсутня мережа, застосунок не зміг перейти у онлайн",
                });
            } else {
                dispatch(updateOfflineStatus(false));
            }
        } else {
            dispatch(updateOfflineStatus(true));
        }

        setOpenModal(false);
    }, [notification, dispatch, offlineMode]);

    const content = () => (
        <Flex vertical align="center" className="network-indicator">
            <PrimaryText fontSize={8} textNoWrap lineHeight="120%">
                {latency === "offline" ? "∞" : formatLatency(latency)}
            </PrimaryText>

            {latency === "offline" ? (
                <div
                    dangerouslySetInnerHTML={{
                        __html: latencyIcons[type].off,
                    }}
                    style={{
                        lineHeight: 0.6,
                    }}
                />
            ) : (
                <img src={checkLatencyIcon(type, latency)} alt="latency-icon" />
            )}
        </Flex>
    );

    return (
        <>
            {canChangeOfflineMode ? (
                <Button
                    type="text"
                    style={{ width: 32, padding: 0 }}
                    onTouchEnd={handleDoubleTap}
                >
                    {content()}
                </Button>
            ) : (
                content()
            )}

            <BaseModal
                onClose={handleCloseModal}
                open={openModal}
                title={
                    offlineMode ? "Повернутися в онлайн" : "Перейти в офлайн"
                }
                icon={
                    <StyledExclamationCircleOutlined style={{ fontSize: 24 }} />
                }
                mask={{ closable: true }}
                description={
                    offlineMode
                        ? "Повернемо синхронізацію даних із сервером."
                        : "Дані будуть зберігатися локально та синхронізуються, коли зʼявиться інтернет."
                }
                onConfirm={handleChangeMode}
            />
        </>
    );
};
